import { useState } from "react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil, Trash2, UtensilsCrossed } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Meal {
  meal_time?: string;
  foods: string[];
  calories: number | null;
}

interface MealPlan {
  type: "meal_plan";
  event_date: string;
  text: string;
  risk_level: string;
  created_at: string;
  plan_name: string;
  start_date: string;
  is_main_record: string | boolean;
  structure_type: "weekly";
  day_of_week?: string;
  meal_type?: string;
  meals: Meal[];
  notes?: string | null;
}

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface SmartfileMealPlanDetailsProps {
  session: SmartfileSession;
  onRefresh: () => void;
  onRecordsChange?: (updatedRecords: any[]) => void;
  expandAllByDefault?: boolean;
}

interface EditableMealPlan extends MealPlan {
  _index?: number;
}

// Mapear meal_time para prefixo e label
const getMealPrefix = (mealTime?: string): { prefix: string; label: string } => {
  if (!mealTime) return { prefix: '', label: 'Refeição' };

  const mealTimeMap: Record<string, { prefix: string; label: string }> = {
    'cafe_da_manha': { prefix: 'C:', label: 'Café da manhã' },
    'almoco': { prefix: 'A:', label: 'Almoço' },
    'jantar': { prefix: 'J:', label: 'Jantar' },
    'lanche': { prefix: 'L:', label: 'Lanche' },
    'ceia': { prefix: 'CE:', label: 'Ceia' },
  };

  return mealTimeMap[mealTime.toLowerCase()] || { prefix: '', label: mealTime };
};

// Ordenar refeições por ordem padrão (Café, Almoço, Jantar)
const getMealOrder = (mealTime?: string): number => {
  const order: Record<string, number> = {
    'cafe_da_manha': 1,
    'almoco': 2,
    'lanche': 3,
    'jantar': 4,
    'ceia': 5,
  };
  return order[mealTime?.toLowerCase() || ''] || 999;
};

// Type guard para verificar se é um meal_plan weekly válido
function isWeeklyMealPlan(record: any): record is MealPlan {
  if (typeof record !== 'object' || record === null) return false;
  if (!('type' in record) || record.type !== 'meal_plan') return false;
  const mealPlan = record as { structure_type?: string; is_main_record?: boolean | string };
  // Verificar se é weekly e não é main_record (pode ser boolean ou string "true"/"false")
  const isMainRecord = mealPlan.is_main_record === true || mealPlan.is_main_record === 'true';
  return mealPlan.structure_type === 'weekly' && !isMainRecord;
}

export function SmartfileMealPlanDetails({ session, onRefresh, onRecordsChange, expandAllByDefault = false }: SmartfileMealPlanDetailsProps) {
  // Buscar o main_record
  const mainRecord = (session.structured_records || []).find((record: any) =>
    typeof record === 'object' &&
    record !== null &&
    'type' in record &&
    record.type === 'meal_plan' &&
    ((record as any).is_main_record === true || (record as any).is_main_record === 'true')
  ) as MealPlan | undefined;

  // Filtrar apenas meal_plans (excluir o registro principal is_main_record: true)
  const allRecords = session.structured_records || [];
  console.log(`🔍 [SmartfileMealPlanDetails] Session ${session.id}: ${allRecords.length} total records`);

  const mealPlans: EditableMealPlan[] = allRecords
    .filter(isWeeklyMealPlan)
    .map((record: MealPlan, index: number) => ({
      ...record,
      _index: index
    }));

  console.log(`✅ [SmartfileMealPlanDetails] Filtered ${mealPlans.length} meal_plans from ${allRecords.length} records`);
  console.log(`📋 [SmartfileMealPlanDetails] Sample plan texts:`, mealPlans.slice(0, 3).map(p => p.text));
  console.log(`📋 [SmartfileMealPlanDetails] Sample plan start_dates:`, mealPlans.slice(0, 3).map(p => p.start_date));
  console.log(`📋 [SmartfileMealPlanDetails] Sample plan event_dates:`, mealPlans.slice(0, 3).map(p => p.event_date));

  const [editingDay, setEditingDay] = useState<{
    day: string;
    weekIdx: number;
  } | null>(null);
  const [editingMainRecord, setEditingMainRecord] = useState(false);
  const [editedRecords, setEditedRecords] = useState<EditableMealPlan[]>(mealPlans);
  const [editedMainRecord, setEditedMainRecord] = useState<MealPlan | undefined>(mainRecord);
  const [isSaving, setIsSaving] = useState(false);

  // Atualizar editedRecords quando mealPlans mudar
  React.useEffect(() => {
    setEditedRecords(mealPlans);
    setEditedMainRecord(mainRecord);
  }, [session.structured_records, mainRecord]);

  // Agrupar por semana baseado na repetição de dias
  // Exemplo: 2 SEG = Semana 1 e Semana 2, 3 TER = Semana 1, 2 e 3
  const weekGroups: Array<{ weekNumber: number; weekLabel: string; plans: EditableMealPlan[] }> = [];

  // Primeiro, tentar encontrar semanas explícitas no campo text
  const allRecordsWithWeeks = (session.structured_records || []).filter((r: any) =>
    typeof r === 'object' && r !== null && 'type' in r && r.type === 'meal_plan'
  ) as MealPlan[];

  const weekNumbersFound = new Set<number>();
  allRecordsWithWeeks.forEach(record => {
    const weekMatch = (record.text || '').match(/SEMANA\s*(\d+)/i);
    if (weekMatch) {
      weekNumbersFound.add(parseInt(weekMatch[1]));
    }
  });

  // Se encontrou semanas explícitas no text, usar essa informação
  if (weekNumbersFound.size > 0) {
    const sortedWeekNumbers = Array.from(weekNumbersFound).sort((a, b) => a - b);
    sortedWeekNumbers.forEach(weekNum => {
      weekGroups.push({
        weekNumber: weekNum,
        weekLabel: `SEMANA ${weekNum}`,
        plans: []
      });
    });

    mealPlans.forEach((plan) => {
      const weekMatch = (plan.text || '').match(/SEMANA\s*(\d+)/i);
      if (weekMatch) {
        const weekNum = parseInt(weekMatch[1]);
        const weekGroup = weekGroups.find(g => g.weekNumber === weekNum);
        if (weekGroup) {
          weekGroup.plans.push(plan);
        }
      } else if (weekGroups.length > 0) {
        weekGroups[0].plans.push(plan);
      }
    });
  } else {
    // Detectar múltiplas semanas pela repetição de dias
    // Contar quantas vezes cada dia aparece
    const dayCounts = new Map<string, number>();
    mealPlans.forEach(plan => {
      if (plan.day_of_week) {
        dayCounts.set(plan.day_of_week, (dayCounts.get(plan.day_of_week) || 0) + 1);
      }
    });

    // O número máximo de repetições indica quantas semanas existem
    const maxDayCount = Math.max(...Array.from(dayCounts.values()), 0);
    const numberOfWeeks = maxDayCount > 0 ? maxDayCount : 1;

    console.log(`📊 [SmartfileMealPlanDetails] Day counts:`, Object.fromEntries(dayCounts));
    console.log(`📊 [SmartfileMealPlanDetails] Max day count: ${maxDayCount}`);
    console.log(`📊 [SmartfileMealPlanDetails] Detected ${numberOfWeeks} week(s) based on day repetition`);
    console.log(`📊 [SmartfileMealPlanDetails] Total meal plans: ${mealPlans.length}`);
    console.log(`📊 [SmartfileMealPlanDetails] Meal plans by day:`, mealPlans.map(p => ({ day: p.day_of_week, text: p.text })));

    // Criar grupos de semana
    for (let weekNum = 1; weekNum <= numberOfWeeks; weekNum++) {
      weekGroups.push({
        weekNumber: weekNum,
        weekLabel: `SEMANA ${weekNum}`,
        plans: []
      });
    }

    // Agrupar planos por ordem de aparição de cada dia
    // Primeira ocorrência de cada dia = Semana 1, segunda = Semana 2, etc.
    // IMPORTANTE: Manter a ordem original dos registros (não ordenar por dia da semana)
    const dayOccurrenceMap = new Map<string, number>(); // Rastreia qual ocorrência de cada dia estamos processando

    // Processar planos na ordem original (preservar ordem de aparição no texto/imagem)
    mealPlans.forEach((plan) => {
      if (plan.day_of_week) {
        const currentOccurrence = dayOccurrenceMap.get(plan.day_of_week) || 0;
        const weekNum = currentOccurrence + 1;

        if (weekNum <= numberOfWeeks) {
          const weekGroup = weekGroups.find(g => g.weekNumber === weekNum);
          if (weekGroup) {
            weekGroup.plans.push(plan);
            dayOccurrenceMap.set(plan.day_of_week, currentOccurrence + 1);
          }
        }
      }
    });

    // Após agrupar, ordenar os planos dentro de cada semana por dia da semana
    // para exibição consistente na tabela
    weekGroups.forEach(weekGroup => {
      weekGroup.plans.sort((a, b) => {
        const daysOrder: Record<string, number> = {
          'SEG': 1, 'TER': 2, 'QUA': 3, 'QUI': 4, 'SEX': 5, 'SAB': 6, 'DOM': 7
        };
        const dayA = daysOrder[a.day_of_week || ''] || 999;
        const dayB = daysOrder[b.day_of_week || ''] || 999;
        return dayA - dayB;
      });
    });
  }

  // Ordenar semanas por número
  weekGroups.sort((a, b) => a.weekNumber - b.weekNumber);

  // Se não houver semanas, criar uma padrão
  if (weekGroups.length === 0) {
    weekGroups.push({
      weekNumber: 1,
      weekLabel: 'SEMANA 1',
      plans: mealPlans
    });
  }

  console.log(`📊 [SmartfileMealPlanDetails] Grouped into ${weekGroups.length} week(s):`, weekGroups.map(w => ({ label: w.weekLabel, plansCount: w.plans.length })));

  // Obter todos os dias únicos ordenados
  const allDays = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB', 'DOM'];
  const daysOrder: Record<string, number> = {
    'SEG': 1, 'TER': 2, 'QUA': 3, 'QUI': 4, 'SEX': 5, 'SAB': 6, 'DOM': 7
  };

  // Obter dias presentes nos planos
  const presentDays = new Set(mealPlans.map(p => p.day_of_week).filter(Boolean) as string[]);
  const sortedDays = allDays.filter(day => presentDays.has(day));

  console.log(`📊 [SmartfileMealPlanDetails] Grouped into ${weekGroups.length} week(s):`, weekGroups.map(w => w.weekLabel));

  const handleSaveDay = async (day: string) => {
    if (!editingDay || editingDay.day !== day) return;

    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Salvar todas as semanas deste dia
      const plansToSave: Array<{ planIndex: number; realPlanIndex: number }> = [];

      weekGroups.forEach((weekGroup, weekIdx) => {
        const plan = weekGroup.plans.find(p => p.day_of_week === day);
        if (!plan) return;

        const planIndex = editedRecords.findIndex(p => p._index === plan._index);
        if (planIndex === -1) return;

        // Encontrar o índice real do plano no array completo
        let mealPlanCount = 0;
        let realPlanIndex = -1;

        for (let i = 0; i < updatedRecords.length; i++) {
          const record = updatedRecords[i];
          if (isWeeklyMealPlan(record)) {
            if (mealPlanCount === planIndex) {
              realPlanIndex = i;
              break;
            }
            mealPlanCount++;
          }
        }

        if (realPlanIndex !== -1) {
          plansToSave.push({ planIndex, realPlanIndex });
        }
      });

      // Atualizar todos os planos deste dia
      plansToSave.forEach(({ planIndex, realPlanIndex }) => {
        const updatedPlan = { ...editedRecords[planIndex] };
        updatedRecords[realPlanIndex] = updatedPlan;
      });

      if (plansToSave.length > 0) {
        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        // Chamar onRecordsChange se fornecido (para Step2Analyze)
        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success(`✅ Dia ${day} atualizado com sucesso em ${plansToSave.length} semana(s)!`);
        setEditingDay(null);
        onRefresh();
      } else {
        toast.error("❌ Nenhum plano encontrado para salvar");
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMainRecord = async () => {
    if (!editedMainRecord) return;

    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];
      const mainRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'meal_plan' &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true')
      );

      if (mainRecordIndex >= 0) {
        updatedRecords[mainRecordIndex] = editedMainRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Título do plano atualizado com sucesso!");
        setEditingMainRecord(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteMeal = async (planIndex: number, mealIndex: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice real do plano no array completo
      let mealPlanCount = 0;
      let realPlanIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (isWeeklyMealPlan(record)) {
          if (mealPlanCount === planIndex) {
            realPlanIndex = i;
            break;
          }
          mealPlanCount++;
        }
      }

      if (realPlanIndex !== -1) {
        const updatedPlan = { ...(updatedRecords[realPlanIndex] as MealPlan) };
        updatedPlan.meals = updatedPlan.meals.filter((_, idx) => idx !== mealIndex);
        updatedRecords[realPlanIndex] = updatedPlan;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        // Chamar onRecordsChange se fornecido (para Step2Analyze)
        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Refeição excluída com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir refeição");
    }
  };

  const handleCancel = () => {
    setEditedRecords(mealPlans);
    setEditingDay(null);
  };

  if (mealPlans.length === 0) {
    console.log(`⚠️ [SmartfileMealPlanDetails] No meal_plans found. Total records: ${allRecords.length}`);
    // Log dos tipos de registros encontrados
    const recordTypes = allRecords.map((r: any) => {
      if (typeof r === 'object' && r !== null && 'type' in r) {
        const rec = r as { type?: string; structure_type?: string; is_main_record?: boolean | string };
        return {
          type: rec.type,
          structure_type: rec.structure_type,
          is_main_record: rec.is_main_record,
          isMainRecord: rec.is_main_record === true || rec.is_main_record === 'true'
        };
      }
      return 'any';
    });
    console.log(`📋 [SmartfileMealPlanDetails] Record types:`, recordTypes);

    return (
      <div className="text-center py-8 text-muted-foreground bg-background border-t">
        <p className="text-sm">Nenhum plano alimentar encontrado nesta sessão</p>
        <p className="text-xs mt-2">Total de registros: {allRecords.length}</p>
      </div>
    );
  }

  // Função auxiliar para encontrar plano de um dia específico em uma semana
  const getPlanForDay = (weekGroup: typeof weekGroups[0], day: string): EditableMealPlan | undefined => {
    return weekGroup.plans.find(p => p.day_of_week === day);
  };

  // Obter número máximo de refeições por dia (para calcular rowSpan)
  const maxMealsPerDay = Math.max(...mealPlans.map(p => p.meals.length), 0);

  return (
    <div className="bg-background border-t">
      {/* Header com título editável */}
      {mainRecord && (
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              {editingMainRecord ? (
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex items-center gap-2 flex-1">
                    <Label className="text-xs">Título:</Label>
                    <Input
                      value={editedMainRecord?.plan_name || ''}
                      onChange={(e) => {
                        setEditedMainRecord({
                          ...editedMainRecord!,
                          plan_name: e.target.value
                        });
                      }}
                      className="h-8 text-sm"
                      placeholder="Nome do plano"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="text-lg font-bold">
                    {mainRecord?.plan_name || 'Plano Alimentar'}
                  </h3>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {editingMainRecord ? (
                <>
                  <Button
                    size="sm"
                    onClick={handleSaveMainRecord}
                    disabled={isSaving}
                    className="h-8"
                  >
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingMainRecord(false);
                      setEditedMainRecord(mainRecord);
                    }}
                    disabled={isSaving}
                    className="h-8"
                  >
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingMainRecord(true);
                  }}
                  className="h-8"
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Editar
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="p-4 overflow-x-auto">
        <div className="min-w-full inline-block">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-r bg-muted/50 sticky left-0 z-10 min-w-[80px]">
                  Dia
                </th>
                {weekGroups.map((weekGroup) => (
                  <th
                    key={weekGroup.weekNumber}
                    className="p-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b bg-muted/50 min-w-[250px]"
                  >
                    {weekGroup.weekLabel}
                  </th>
                ))}
                <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b bg-muted/50 min-w-[100px]">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedDays.map((day) => {
                // Encontrar planos deste dia em cada semana
                const dayPlansByWeek = weekGroups.map(weekGroup => getPlanForDay(weekGroup, day));
                const hasAnyPlan = dayPlansByWeek.some(p => p !== undefined);

                if (!hasAnyPlan) return null;

                return (
                  <tr key={day} className="border-b border-border hover:bg-muted/30 transition-colors">
                    <td className="p-3 text-sm font-medium align-top border-r bg-muted/20 sticky left-0 z-10">
                      {day}
                    </td>
                    {weekGroups.map((weekGroup, weekIdx) => {
                      const plan = getPlanForDay(weekGroup, day);

                      if (!plan) {
                        return (
                          <td key={weekGroup.weekNumber} className="p-3 text-sm text-muted-foreground/50 border-r">
                            –
                          </td>
                        );
                      }

                      // Ordenar refeições por meal_time
                      const sortedMeals = [...plan.meals].sort((a, b) =>
                        getMealOrder(a.meal_time) - getMealOrder(b.meal_time)
                      );

                      const originalIndex = plan._index;
                      const planIndex = editedRecords.findIndex(p => p._index === originalIndex);
                      // Se o dia está sendo editado, todas as semanas deste dia ficam editáveis
                      const isEditingThisDay = editingDay?.day === day;

                      return (
                        <td key={weekGroup.weekNumber} className="p-3 text-sm border-r align-top">
                          {isEditingThisDay ? (
                            <div className="space-y-3 p-2 bg-muted/30 rounded border">
                              {sortedMeals.map((meal, mealIdx) => {
                                const realMealIdx = plan.meals.indexOf(meal);
                                const mealPrefix = getMealPrefix(meal.meal_time);

                                return (
                                  <div key={realMealIdx} className="space-y-2">
                                    <div>
                                      <Label className="text-xs">{mealPrefix.prefix} {mealPrefix.label}</Label>
                                      <Input
                                        value={editedRecords[planIndex]?.meals[realMealIdx]?.foods?.join(', ') || ''}
                                        onChange={(e) => {
                                          const updated = [...editedRecords];
                                          updated[planIndex] = {
                                            ...updated[planIndex],
                                            meals: updated[planIndex].meals.map((m, idx) =>
                                              idx === realMealIdx
                                                ? {
                                                  ...m,
                                                  foods: e.target.value.split(',').map(f => f.trim()).filter(Boolean)
                                                }
                                                : m
                                            )
                                          };
                                          setEditedRecords(updated);
                                        }}
                                        className="h-8 text-xs mt-1"
                                        placeholder="Ex: MINGAU DE AVEIA"
                                      />
                                    </div>
                                    <div className="flex gap-1">
                                      <Input
                                        type="number"
                                        value={editedRecords[planIndex]?.meals[realMealIdx]?.calories ?? ''}
                                        onChange={(e) => {
                                          const updated = [...editedRecords];
                                          updated[planIndex] = {
                                            ...updated[planIndex],
                                            meals: updated[planIndex].meals.map((m, idx) =>
                                              idx === realMealIdx
                                                ? {
                                                  ...m,
                                                  calories: e.target.value ? parseFloat(e.target.value) : null
                                                }
                                                : m
                                            )
                                          };
                                          setEditedRecords(updated);
                                        }}
                                        className="h-8 text-xs flex-1"
                                        placeholder="Calorias"
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="space-y-1">
                              {sortedMeals.map((meal, mealIdx) => {
                                const realMealIdx = plan.meals.indexOf(meal);
                                const mealPrefix = getMealPrefix(meal.meal_time);

                                return (
                                  <div key={realMealIdx} className="text-xs">
                                    <span className="font-medium">{mealPrefix.prefix}</span>{' '}
                                    {meal.foods && meal.foods.length > 0 ? (
                                      meal.foods.join(', ')
                                    ) : (
                                      <span className="text-muted-foreground">–</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="p-3 text-right align-top">
                      <div className="flex gap-1 justify-end">
                        {/* Botão de editar - apenas um por linha (por dia) */}
                        {dayPlansByWeek.some(p => p !== undefined) && (
                          <>
                            {editingDay?.day === day ? (
                              // Se está editando este dia, mostrar botões de salvar/cancelar
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveDay(day)}
                                  disabled={isSaving}
                                  className="h-7 px-2"
                                >
                                  {isSaving ? "Salvando..." : "Salvar"}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleCancel}
                                  disabled={isSaving}
                                  className="h-7 px-2"
                                >
                                  Cancelar
                                </Button>
                              </div>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    // Ativar edição do dia (primeira semana que tiver o dia)
                                    const firstWeekIdx = dayPlansByWeek.findIndex(p => p !== undefined);
                                    if (firstWeekIdx !== -1) {
                                      setEditingDay({ day, weekIdx: firstWeekIdx });
                                    }
                                  }}
                                  className="h-7 px-2"
                                  title={`Editar todas as refeições de ${day}`}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                {/* Botão de excluir dia completo */}
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                      title={`Excluir dia ${day} de todas as semanas`}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Tem certeza que deseja excluir o dia {day} de todas as semanas?
                                        <br />
                                        <span className="font-medium mt-2 block text-xs">
                                          Esta ação excluirá todas as refeições deste dia em todas as semanas.
                                        </span>
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={async () => {
                                          // Excluir todos os planos deste dia
                                          for (const plan of dayPlansByWeek) {
                                            if (plan) {
                                              // Encontrar índice do plano
                                              const planIndex = editedRecords.findIndex(p => p._index === plan._index);
                                              if (planIndex !== -1) {
                                                await handleDeleteMeal(planIndex, 0); // Excluir o primeiro, depois recarregar
                                              }
                                            }
                                          }
                                          onRefresh();
                                        }}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        Excluir
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
        Total: {mealPlans.length} dia(s) de plano alimentar em {weekGroups.length} semana(s)
      </div>
    </div>
  );
}

